/**
 * Admin AI Settings Page
 *
 * Configure AI provider, model, and parameters for the chatbot.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import { AI_PROVIDERS } from '@petmas/shared';

// UI constants
const SAVE_MESSAGE_DISPLAY_DURATION_MS = 3000;
const TEST_RESPONSE_PREVIEW_LENGTH = 100;

// Default AI configuration values
const DEFAULT_TEMPERATURE = 0.7;
const DEFAULT_MAX_TOKENS = 1024;
const DEFAULT_MODEL = '@cf/meta/llama-3.1-8b-instruct';

// Temperature slider range
const TEMPERATURE_MIN = 0;
const TEMPERATURE_MAX = 1;
const TEMPERATURE_STEP = 0.1;

interface AISettings {
  ai_provider: string;
  ai_model: string;
  ai_temperature: number;
  ai_max_tokens: number;
  ai_enabled: boolean;
}

const PROVIDER_MODELS: Record<string, Array<{ value: string; label: string }>> = {
  [AI_PROVIDERS.CLOUDFLARE]: [
    { value: '@cf/meta/llama-3.1-8b-instruct', label: 'Llama 3.1 8B Instruct' },
    { value: '@cf/meta/llama-3.1-70b-instruct', label: 'Llama 3.1 70B Instruct' },
    { value: '@cf/mistral/mistral-7b-instruct-v0.1', label: 'Mistral 7B Instruct' },
    { value: '@hf/thebloke/deepseek-coder-6.7b-instruct-awq', label: 'DeepSeek Coder 6.7B' },
  ],
  [AI_PROVIDERS.OPENAI]: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Recomendado)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  [AI_PROVIDERS.ANTHROPIC]: [
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (Recomendado)' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
  ],
  [AI_PROVIDERS.GROK]: [
    { value: 'grok-2', label: 'Grok 2 (Recomendado)' },
    { value: 'grok-2-mini', label: 'Grok 2 Mini' },
    { value: 'grok-beta', label: 'Grok Beta' },
  ],
  [AI_PROVIDERS.GLM]: [
    { value: 'glm-4', label: 'GLM-4 (Recomendado)' },
    { value: 'glm-4-flash', label: 'GLM-4 Flash' },
    { value: 'glm-3-turbo', label: 'GLM-3 Turbo' },
  ],
};

const PROVIDER_INFO: Record<string, { name: string; description: string; requiresKey: boolean }> = {
  [AI_PROVIDERS.CLOUDFLARE]: {
    name: 'Cloudflare Workers AI',
    description: 'Incluido con Cloudflare Workers. Sin costo adicional.',
    requiresKey: false,
  },
  [AI_PROVIDERS.OPENAI]: {
    name: 'OpenAI',
    description: 'GPT-4 y GPT-3.5. Requiere clave API de OpenAI.',
    requiresKey: true,
  },
  [AI_PROVIDERS.ANTHROPIC]: {
    name: 'Anthropic Claude',
    description: 'Claude 3 (Opus, Sonnet, Haiku). Requiere clave API.',
    requiresKey: true,
  },
  [AI_PROVIDERS.GROK]: {
    name: 'xAI Grok',
    description: 'Grok 2 de xAI. Requiere clave API.',
    requiresKey: true,
  },
  [AI_PROVIDERS.GLM]: {
    name: 'Zhipu GLM',
    description: 'GLM-4 de Zhipu AI. Requiere clave API.',
    requiresKey: true,
  },
};

const DEFAULT_SETTINGS: AISettings = {
  ai_provider: AI_PROVIDERS.CLOUDFLARE,
  ai_model: DEFAULT_MODEL,
  ai_temperature: DEFAULT_TEMPERATURE,
  ai_max_tokens: DEFAULT_MAX_TOKENS,
  ai_enabled: true,
};

export function AdminAISettingsPage() {
  const { getSettings, updateSettings, testAIConnection, isLoading, error } = useAdminApi();
  const [settings, setSettings] = useState<AISettings>(DEFAULT_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Load settings on mount
  useEffect(() => {
    async function loadSettings() {
      const result = await getSettings('ai');
      if (result) {
        setSettings({
          ai_provider: (result.ai_provider as string) ?? DEFAULT_SETTINGS.ai_provider,
          ai_model: (result.ai_model as string) ?? DEFAULT_SETTINGS.ai_model,
          ai_temperature: (result.ai_temperature as number) ?? DEFAULT_SETTINGS.ai_temperature,
          ai_max_tokens: (result.ai_max_tokens as number) ?? DEFAULT_SETTINGS.ai_max_tokens,
          ai_enabled: (result.ai_enabled as boolean) ?? DEFAULT_SETTINGS.ai_enabled,
        });
      }
    }
    loadSettings();
  }, [getSettings]);

  const handleProviderChange = useCallback((provider: string) => {
    const models = PROVIDER_MODELS[provider];
    const defaultModel = models?.[0]?.value ?? '';
    setSettings((prev) => ({
      ...prev,
      ai_provider: provider,
      ai_model: defaultModel,
    }));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const success = await updateSettings('ai', { ...settings });
      if (success) {
        setSaveMessage('Configuracion guardada exitosamente');
        setTimeout(() => setSaveMessage(null), SAVE_MESSAGE_DISPLAY_DURATION_MS);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testAIConnection(settings.ai_provider, settings.ai_model);
      if (result.success && result.response) {
        setTestResult({
          success: true,
          message: `Conexion exitosa. Respuesta: "${result.response.slice(0, TEST_RESPONSE_PREVIEW_LENGTH)}..."`,
        });
      } else {
        setTestResult({
          success: false,
          message: result.error ?? 'Error al probar la conexion',
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Error de conexion',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const availableModels = PROVIDER_MODELS[settings.ai_provider] ?? [];
  const providerInfo = PROVIDER_INFO[settings.ai_provider];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuracion de IA</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configura el proveedor y modelo de inteligencia artificial para el chatbot.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="px-4 py-2 text-white rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-md bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {saveMessage && (
        <div className="p-4 rounded-md bg-green-50">
          <p className="text-sm text-green-700">{saveMessage}</p>
        </div>
      )}

      {/* AI Enabled Toggle */}
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Chatbot de IA</h2>
            <p className="text-sm text-gray-500">
              Activa o desactiva el chatbot de inteligencia artificial.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.ai_enabled}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, ai_enabled: e.target.checked }))
              }
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      {/* Provider Selection */}
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Proveedor de IA</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(PROVIDER_INFO).map(([key, info]) => (
            <label
              key={key}
              className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                settings.ai_provider === key
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value={key}
                checked={settings.ai_provider === key}
                onChange={() => handleProviderChange(key)}
                className="sr-only"
              />
              <span className="font-medium text-gray-900">{info.name}</span>
              <span className="mt-1 text-sm text-gray-500">{info.description}</span>
              {info.requiresKey && (
                <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  Requiere clave API
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Model Selection */}
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Modelo</h2>
        {providerInfo?.requiresKey && (
          <div className="mb-4 p-3 rounded-md bg-yellow-50 border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Nota:</strong> Asegurate de configurar la clave API correspondiente en las
              variables de entorno del worker (OPENAI_API_KEY, ANTHROPIC_API_KEY, etc.).
            </p>
          </div>
        )}
        <select
          value={settings.ai_model}
          onChange={(e) => setSettings((prev) => ({ ...prev, ai_model: e.target.value }))}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          {availableModels.map((model) => (
            <option key={model.value} value={model.value}>
              {model.label}
            </option>
          ))}
        </select>
      </div>

      {/* Parameters */}
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Parametros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperatura: {settings.ai_temperature}
            </label>
            <input
              type="range"
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              value={settings.ai_temperature}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, ai_temperature: parseFloat(e.target.value) }))
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Mas preciso</span>
              <span>Mas creativo</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <select
              value={settings.ai_max_tokens}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, ai_max_tokens: parseInt(e.target.value) }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="512">512 (Corto)</option>
              <option value="1024">1024 (Normal)</option>
              <option value="2048">2048 (Largo)</option>
              <option value="4096">4096 (Muy largo)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Longitud maxima de las respuestas del chatbot.
            </p>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Probar Conexion</h2>
        <p className="text-sm text-gray-500 mb-4">
          Verifica que la configuracion funciona correctamente enviando un mensaje de prueba.
        </p>
        <button
          onClick={handleTestConnection}
          disabled={isTesting}
          className="px-4 py-2 text-white rounded-md bg-gray-700 hover:bg-gray-800 disabled:opacity-50"
        >
          {isTesting ? 'Probando...' : 'Probar Conexion'}
        </button>

        {testResult && (
          <div
            className={`mt-4 p-4 rounded-md ${
              testResult.success ? 'bg-green-50' : 'bg-red-50'
            }`}
          >
            <div className="flex items-center">
              {testResult.success ? (
                <CheckIcon className="h-5 w-5 text-green-400" />
              ) : (
                <XIcon className="h-5 w-5 text-red-400" />
              )}
              <p
                className={`ml-2 text-sm ${
                  testResult.success ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {testResult.message}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Current Configuration Summary */}
      <div className="p-6 bg-gray-50 rounded-lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Configuracion</h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <dt className="text-gray-500">Estado:</dt>
          <dd className="font-medium">{settings.ai_enabled ? 'Activo' : 'Desactivado'}</dd>
          <dt className="text-gray-500">Proveedor:</dt>
          <dd className="font-medium">{PROVIDER_INFO[settings.ai_provider]?.name}</dd>
          <dt className="text-gray-500">Modelo:</dt>
          <dd className="font-medium font-mono text-xs">{settings.ai_model}</dd>
          <dt className="text-gray-500">Temperatura:</dt>
          <dd className="font-medium">{settings.ai_temperature}</dd>
          <dt className="text-gray-500">Max Tokens:</dt>
          <dd className="font-medium">{settings.ai_max_tokens}</dd>
        </dl>
      </div>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
